import { useState, useEffect } from "react";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { ChipDownContainer, Dropdown, DropdownButton, EllipsisDropdownItem } from "@similarweb/ui-components";
import { Button } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { FilterItemButton } from "app/pages/website-analysis/traffic-sources/search/components/filters/WebsiteKeywordsPageFiltersStyledComponents";
import styled from "styled-components";
import { setFontVariant } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { FlexRow } from "app/pages/website-analysis/website-content/leading-folders/components/Tab";
import { DomainCell } from "app/components/React/Table/cells";
import { AdvancedFilterButton } from "pro-features/components/filtersPanel/src/filtersPanel";
import { SimpleChipItemWrap } from "@similarweb/ui-components";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { allTrackers } from "app/services/track/track";

// region styled components

const ModalTitle = styled.div`
    ${setFontVariant("h5", colorsPalettes.carbon[500])};
    margin-bottom: 36px;
`;

const ModalSubTitle = styled.div`
    ${setFontVariant("body1", colorsPalettes.carbon[400])};
    margin-bottom: 16px;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const CloseButton = styled(Button)`
    margin-right: 8px;
`;

const AndText = styled.div`
    height: 20px;
    width: 30px;
    font-size: 14px;
    font-weight: 400;
`;

const ModalTier = styled.div`
    display: grid;
    grid-template-columns: 43% 57%;
    padding-bottom: 16px;
`;

const CUSTOM_MODAL_STYLES = {
    content: {
        padding: "24px 20px 16px 24px",
        width: 400,
        border: 0,
    },
};

// endregion

export enum PositionTrends {
    Increased = "increased",
    Decreased = "decreased",
    NoChange = "noChange",
    AnyChange = "anyChange",
    PositionZero = "0-0",
    PositionOneHundredOne = "101-101",
}

// region text

const getModalTitle = () => translate("postition.trend.filter.modal.title");
const getModalSubtitle = () => translate("postition.trend.filter.modal.sub.title");
const getDropdownPlaceholderText = () => translate("change.filter.dropdown.placeholder");

const getAndText = () => translate("ranking.distribution.compare.filters.and");

const getPositionTrendsButtonText = () => translate("ranking.distribution.change.filter.selected.text");

const getPositionTrendsButtonMixedText = () => translate("ranking.distribution.change.filter.selected.mix.text");

const getPositionTrendsButtonAllCompetitorsImprovedText = () =>
    translate("ranking.distribution.change.filter.selected.all.improved.text");

const getPositionTrendsButtonAllCompetitorsDeclinedText = () =>
    translate("ranking.distribution.change.filter.selected.all.declined.text");

const getPositionTrendsButtonAllCompetitorsNoChangeText = () =>
    translate("ranking.distribution.change.filter.selected.all.no.change.text");

const getApplyText = () => translate("ranking.filter.modal.apply.button");

const getCloseText = () => translate("ranking.filter.modal.close.button");

const getPositionTrendsTexts = () => ({
    [PositionTrends.Increased]: translate("ranking.distribution.change.filter.increased"),
    [PositionTrends.Decreased]: translate("ranking.distribution.change.filter.decreased"),
    [PositionTrends.NoChange]: translate("ranking.distribution.change.filter.no.change"),
    [PositionTrends.AnyChange]: translate("ranking.distribution.change.filter.any.change"),
});

const getButtonTextToPositionValue = (presetSelectedModalItems, chosenItems, queryItems) => {
    if (!presetSelectedModalItems) {
        return getPositionTrendsButtonText();
    }
    const values = Object.values(presetSelectedModalItems);
    const areAllValuesIncrease = values.every((value) => value === PositionTrends.Increased);
    const areAllValuesDecrease = values.every((value) => value === PositionTrends.Decreased);
    const areAllValuesNoChange = values.every((value) => value === PositionTrends.NoChange);
    if (values.length === chosenItems.length) {
        if (areAllValuesIncrease) {
            return getPositionTrendsButtonAllCompetitorsImprovedText();
        }
        if (areAllValuesDecrease) {
            return getPositionTrendsButtonAllCompetitorsDeclinedText();
        }
        if (areAllValuesNoChange) {
            return getPositionTrendsButtonAllCompetitorsNoChangeText();
        }
    }
    if (Object.keys(presetSelectedModalItems).length === 1) {
        if (areAllValuesIncrease) {
            return translate("ranking.distribution.change.filter.selected.multiple.improved.text.single.site", {
                site: queryItems[0].url,
            });
        }
        if (areAllValuesDecrease) {
            return translate("ranking.distribution.change.filter.selected.multiple.declined.text.single.site", {
                site: queryItems[0].url,
            });
        }
        if (areAllValuesNoChange) {
            return translate("ranking.distribution.change.filter.selected.multiple.no.change.text.single.site", {
                site: queryItems[0].url,
            });
        }
    }
    if (areAllValuesIncrease) {
        return translate("ranking.distribution.change.filter.selected.multiple.improved.text", {
            site: queryItems[0].url,
            numberOfCompetitors: String(values.length - 1),
        });
    }
    if (areAllValuesDecrease) {
        return translate("ranking.distribution.change.filter.selected.multiple.declined.text", {
            site: queryItems[0].url,
            numberOfCompetitors: String(values.length - 1),
        });
    }
    if (areAllValuesNoChange) {
        return translate("ranking.distribution.change.filter.selected.multiple.no.change.text", {
            site: queryItems[0].url,
            numberOfCompetitors: String(values.length - 1),
        });
    }
    return getPositionTrendsButtonMixedText();
};

// endregion

const POSITION_TRENDS_DROPDOWN_ITEMS = [
    {
        text: getPositionTrendsTexts()[PositionTrends.Increased],
        key: PositionTrends.Increased,
    },
    {
        text: getPositionTrendsTexts()[PositionTrends.Decreased],
        key: PositionTrends.Decreased,
    },
    {
        text: getPositionTrendsTexts()[PositionTrends.NoChange],
        key: PositionTrends.NoChange,
    },
    {
        text: getPositionTrendsTexts()[PositionTrends.AnyChange],
        key: PositionTrends.AnyChange,
    },
];

const MAP_DROPDOWN_ITEM_KEY_TO_DROPDOWN_ITEM_TEXT = {
    [POSITION_TRENDS_DROPDOWN_ITEMS[0].key]: POSITION_TRENDS_DROPDOWN_ITEMS[0].text,
    [POSITION_TRENDS_DROPDOWN_ITEMS[1].key]: POSITION_TRENDS_DROPDOWN_ITEMS[1].text,
    [POSITION_TRENDS_DROPDOWN_ITEMS[2].key]: POSITION_TRENDS_DROPDOWN_ITEMS[2].text,
    [POSITION_TRENDS_DROPDOWN_ITEMS[3].key]: POSITION_TRENDS_DROPDOWN_ITEMS[3].text,
};

const QUOTATION_AND_BRACKETS_REGEX = /[\"{}]/g;

const extractPositionTrendFilterValuePatternForCompare = (selectedModalItems: Record<string, string>) => {
    return JSON.stringify(selectedModalItems).replace(QUOTATION_AND_BRACKETS_REGEX, "");
};

const CompareTrendFilter = () => {
    const { chosenItems, onPositionTrendChange, tableFilters } = useRankingDistributionTableTopContext();

    const { positionTrends, pageFilter } = tableFilters;
    const queryItems = safeJsonParse(pageFilter, []);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [presetTrends, setPresetTrends] = useState<Record<string, string>>({});
    const [trends, setTrends] = useState<Record<string, string>>({});

    useEffect(() => {
        if (positionTrends) {
            const selectedModalItems = positionTrends.split(",").reduce(
                (trendsAccumulator, siteWithTrend) => {
                    const [site, trend] = siteWithTrend.split(":");
                    return {
                        ...trendsAccumulator,
                        [site]: trend,
                    };
                },
                {} as Record<string, string>,
            );
            const validDomains = queryItems.map((item) => item.url);
            const cleanedTrends = Object.keys(selectedModalItems).reduce(
                (acc, key) => {
                    if (validDomains.includes(key)) {
                        acc[key] = selectedModalItems[key];
                    }
                    return acc;
                },
                {} as Record<string, string>,
            );
            setPresetTrends(cleanedTrends);
            setTrends(cleanedTrends);
        } else {
            setPresetTrends({});
            setTrends({});
        }
    }, [positionTrends, setPresetTrends]);

    const toggleModalOpen = () => {
        if (!isModalOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "open position change filter");
        }
        setIsModalOpen((prevIsModalOpen) => !prevIsModalOpen);
    };

    const resetPresetTrendsAndToggleModal = () => {
        setPresetTrends(trends);
        setIsModalOpen(false);
    };

    const toggleTrendForSite = (domain: string) => (newTrend: { id: PositionTrends; name: string }) => {
        if (newTrend.id === PositionTrends.AnyChange) {
            const { [domain]: _site, ...otherTrends } = presetTrends;

            setPresetTrends(otherTrends);
        } else {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filters/position change/${newTrend.id || ""}`);
            const selectedTrend = POSITION_TRENDS_DROPDOWN_ITEMS.find((trend) => trend.key === newTrend.id);
            const siteWithSelectedTrend = { [domain]: selectedTrend.key };

            setPresetTrends({ ...presetTrends, ...siteWithSelectedTrend });
        }
    };

    const getDropdownChildren = (site: string) => {
        const selectedTrend = presetTrends[site];
        const buttonText = selectedTrend
            ? MAP_DROPDOWN_ITEM_KEY_TO_DROPDOWN_ITEM_TEXT[selectedTrend]
            : getDropdownPlaceholderText();

        return [
            <DropdownButton
                key="button-text"
                width={200}
                height={40}
            >
                {buttonText}
            </DropdownButton>,
            ...POSITION_TRENDS_DROPDOWN_ITEMS.map((item) => {
                return (
                    <EllipsisDropdownItem
                        id={item.key}
                        key={item.key}
                        selected={item.key === (selectedTrend as PositionTrends)}
                    >
                        {item.text}
                    </EllipsisDropdownItem>
                );
            }),
        ];
    };

    const applyTrendFilter = () => {
        setTrends(presetTrends);
        setIsModalOpen(false);
        const positionTrendFilterValue = extractPositionTrendFilterValuePatternForCompare(presetTrends);
        allTrackers.dispatchTrackEvent(
            "Filter Table",
            "click",
            `Filters/position change/${positionTrendFilterValue || ""}`,
        );
        onPositionTrendChange(positionTrendFilterValue);
    };

    const clearPositionTrendFilter = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setPresetTrends({});
        setTrends({});
        onPositionTrendChange("");
    };
    return (
        <>
            <FilterItemButton onClick={toggleModalOpen}>
                {Object.values(presetTrends).length ? (
                    <SimpleChipItemWrap
                        text={getButtonTextToPositionValue(presetTrends, chosenItems, queryItems)}
                        onCloseItem={clearPositionTrendFilter}
                    />
                ) : (
                    <AdvancedFilterButton>{getPositionTrendsButtonText()}</AdvancedFilterButton>
                )}
            </FilterItemButton>
            <ProModal
                isOpen={isModalOpen}
                showCloseIcon={false}
                shouldCloseOnOverlayClick={true}
                customStyles={CUSTOM_MODAL_STYLES}
            >
                <ModalTitle>{getModalTitle()}</ModalTitle>
                <ModalSubTitle>{getModalSubtitle()}</ModalSubTitle>
                <ModalContent>
                    {queryItems.map((site, index) => {
                        const selectedTrendKey = presetTrends[site.url];
                        const siteTrendsId = selectedTrendKey ? { [selectedTrendKey]: true } : {};
                        return (
                            <ModalTier key={index}>
                                <FlexRow>
                                    <AndText>{index !== 0 ? getAndText() : ""}</AndText>
                                    <DomainCell field={site.url} />
                                </FlexRow>
                                <Dropdown
                                    selectedIds={siteTrendsId}
                                    onClick={toggleTrendForSite(site.url)}
                                    buttonWidth={212}
                                    closeOnItemClick={true}
                                    itemsComponent={EllipsisDropdownItem}
                                    dropdownPopupHeight={300}
                                >
                                    {getDropdownChildren(site.url)}
                                </Dropdown>
                            </ModalTier>
                        );
                    })}
                    <ModalFooter>
                        <CloseButton
                            type={"flat"}
                            onClick={resetPresetTrendsAndToggleModal}
                        >
                            {getCloseText()}
                        </CloseButton>
                        <Button
                            type={"primary"}
                            onClick={applyTrendFilter}
                        >
                            {getApplyText()}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </ProModal>
        </>
    );
};

// region single mode

const POSITION_TREND_VALUE_REGEX_PATTERN = `${PositionTrends.Increased}|${PositionTrends.Decreased}|${PositionTrends.NoChange}`;
const POSITION_TREND_VALUE_REGEX = new RegExp(POSITION_TREND_VALUE_REGEX_PATTERN, "g");

const getSelectedTextForSingle = (positionTrendFilterValue: string) => {
    if (!positionTrendFilterValue) {
        return "";
    }
    const [trend] = positionTrendFilterValue.match(POSITION_TREND_VALUE_REGEX);
    return getPositionTrendsTexts()[trend];
};

const buildPositionTrendFilterValuePatternForSingle = (domain: string, itemId: string) => `${domain}:${itemId}`;

const SingleTrendFilter = () => {
    const { tableFilters, onPositionTrendChange } = useRankingDistributionTableTopContext();
    const { positionTrends, pageFilter } = tableFilters;
    const queryItems = safeJsonParse(pageFilter, []);
    const mainDomain = queryItems[0]?.url || "";

    const applyChangeFilter = (item) => {
        if (item.id !== PositionTrends.AnyChange) {
            allTrackers.dispatchTrackEvent("Filter Table", "click", `Filters/position change/${item.id || ""}`);
            const positionTrendsFilterValue = buildPositionTrendFilterValuePatternForSingle(mainDomain, item.id);
            onPositionTrendChange(positionTrendsFilterValue);
        } else {
            onPositionTrendChange(null);
        }
    };

    const trackPositionFilterOpen = (isOpen) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "open position change filter");
        }
    };

    return (
        <ChipDownContainer
            onClick={applyChangeFilter}
            onToggle={trackPositionFilterOpen}
            tooltipDisabled={false}
            selectedText={getSelectedTextForSingle(positionTrends)}
            onCloseItem={() => onPositionTrendChange(null)}
            buttonText={getPositionTrendsButtonText()}
            noResultsMessage={translate("dropdown.no.results")}
        >
            {POSITION_TRENDS_DROPDOWN_ITEMS.map((item) => {
                return (
                    <EllipsisDropdownItem
                        id={item.key}
                        key={item.key}
                    >
                        {item.text}
                    </EllipsisDropdownItem>
                );
            })}
        </ChipDownContainer>
    );
};

// endregion

export const PositionTrendsFilterV2 = () => {
    const { isCompare } = useRankingDistributionTableTopContext();

    return isCompare ? <CompareTrendFilter /> : <SingleTrendFilter />;
};
